import {
  DataTable,
  DateField,
  ExportButton,
  List,
  ReferenceField,
  TextField
} from "@/components/admin";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Calendar, FileText, Filter, GitBranch } from "lucide-react";
import { useListContext, useRecordContext } from "ra-core";
import { useCallback, useMemo } from "react";


import { TopToolbar } from "../layout/TopToolbar";
import { PermissionCreateButton } from "./shared/PermissionCreateButton";
import { StatusBadge } from "./shared/StatusBadge";

const StatusCell = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <StatusBadge status={record.status} />;
};

const AmountCell = () => {
  const record = useRecordContext();
  if (!record?.total_amount)
    return <span className="text-slate-400 text-xs">—</span>;
  return (
    <div className="flex items-center gap-1 font-bold text-green-700">
      {/* QTN-3: removed $ DollarSign icon — INR format already shows ₹ (was "$ ₹...") */}
      {new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(record.total_amount)}
    </div>
  );
};

import { getGlobalFilters } from './shared/GlobalFilterBar';

const quotationStatusChoices = [
  { id: "draft", name: "Draft" },
  { id: "sent", name: "Sent" },
  { id: "approved", name: "Approved" },
  { id: "revised", name: "Revised" },
  { id: "rejected", name: "Rejected" },
];

const QuotationListActions = () => (
  <TopToolbar>
    <ExportButton />
    <PermissionCreateButton permission="manage_documents" label="New Quotation" />
  </TopToolbar>
);


const RevisionCell = () => {
  const record = useRecordContext();
  if (!record) return null;
  const revNum = record.revision_number as number | undefined;
  const isRevision = revNum && revNum > 0;
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono font-bold text-blue-600">{record.reference}</span>
      {isRevision && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-700 bg-amber-50 font-semibold flex items-center gap-1">
          <GitBranch className="w-2.5 h-2.5" />
          R{revNum}
        </Badge>
      )}
    </div>
  );
};

const QuotationStatusStats = () => {
  const { data, total, setFilters, displayedFilters } = useListContext<any>();

  const stats = useMemo(() => {
    const base = {
      draft: 0,
      sent: 0,
      approved: 0,
      revised: 0,
    };
    if (!data) return base;
    return data.reduce((acc, record) => {
      const status = (record.status || "draft") as keyof typeof base;
      if (status in acc) {
        acc[status] += 1;
      }
      return acc;
    }, { ...base });
  }, [data]);

  const handleCardClick = useCallback((statusValue: string | null) => {
    if (statusValue === null) {
      setFilters({}, displayedFilters);
    } else {
      setFilters({ status: statusValue }, displayedFilters);
    }
  }, [setFilters, displayedFilters]);

  if (!data || data.length === 0) return null;

  const cards: {
    key: keyof typeof stats | "total";
    label: string;
    value: number | undefined;
    subtitle?: string;
    accentClass: string;
    filterValue: string | null;
  }[] = [
    {
      key: "total",
      label: "Total Quotations",
      value: total,
      accentClass: "border-slate-200 hover:border-slate-400",
      filterValue: null,
    },
    {
      key: "draft",
      label: "Draft",
      value: stats.draft,
      subtitle: "Saved, not yet sent",
      accentClass: "border-slate-300 hover:border-slate-500",
      filterValue: "draft",
    },
    {
      key: "sent",
      label: "Sent",
      value: stats.sent,
      subtitle: "Dispatched to customer",
      accentClass: "border-blue-300 hover:border-blue-500",
      filterValue: "sent",
    },
    {
      key: "approved",
      label: "Approved",
      value: stats.approved,
      subtitle: "Customer approved",
      accentClass: "border-emerald-300 hover:border-emerald-500",
      filterValue: "approved",
    },
    {
      key: "revised",
      label: "Revised",
      value: stats.revised,
      subtitle: "Revision created",
      accentClass: "border-amber-300 hover:border-amber-500",
      filterValue: "revised",
    },
  ];

  return (
    <div className="mb-4">
      <Card className="border-none shadow-sm bg-slate-50/80">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            <FileText className="w-3 h-3" />
            <span>Quotation Status Overview</span>
          </div>
          <div className="inline-flex items-center gap-1 text-[11px] text-slate-400">
            <Filter className="w-3 h-3" />
            <span>Click a card to filter</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-4 pb-4">
          {cards.map((card) => (
            <button
              key={card.key}
              type="button"
              onClick={() => handleCardClick(card.filterValue)}
              className={`rounded-lg border bg-white px-3 py-2.5 flex flex-col gap-1.5 text-left cursor-pointer transition-all duration-150 ${card.accentClass} hover:shadow-md active:scale-[0.98]`}
            >
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                {card.label}
              </span>
              <span className="text-lg font-bold text-slate-900">
                {card.value ?? 0}
              </span>
              {card.subtitle && (
                <span className="text-[11px] text-slate-400">
                  {card.subtitle}
                </span>
              )}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
};

export const QuotationList = () => (
  <List
    title="Quotations"
    actions={<QuotationListActions />}
    filters={getGlobalFilters(quotationStatusChoices, "Quote Status")}
    sort={{ field: "date", order: "DESC" }}
    className="p-6"
    filtersLayout="two-rows"
  >
    <div className="space-y-3">
      <QuotationStatusStats />
      <Card className="border-none shadow-sm overflow-hidden">
        <DataTable rowClick="show">
          <DataTable.Col
            source="reference"
            label="Quotation No."
          >
            <RevisionCell />
          </DataTable.Col>
          <DataTable.Col label="Company">
            <ReferenceField source="company_id" reference="companies" link={false}>
              <TextField
                source="name"
                className="font-semibold text-slate-700"
              />
            </ReferenceField>
          </DataTable.Col>
          <DataTable.Col
            source="customer_name"
            label="Customer"
            className="text-sm"
          />
          <DataTable.Col label="Date">
            <div className="flex items-center gap-2 text-slate-500 text-xs">
              <Calendar className="w-3 h-3" />
              <DateField source="date" />
            </div>
          </DataTable.Col>
          <DataTable.Col label="Status">
            <StatusCell />
          </DataTable.Col>
          <DataTable.Col label="Total">
            <AmountCell />
          </DataTable.Col>
          <DataTable.Col label="Created">
            <DateField source="created_at" showTime showDate />
          </DataTable.Col>
        </DataTable>
      </Card>
    </div>
  </List>
);
